import React, { useState, useEffect } from 'react';
import { Form, Input, Button, DatePicker, InputNumber, Card, Space, message, Spin, Select, Switch, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import api from '../../services/api';
import localforage from 'localforage';
import { Store, ReviewTask } from '../../types';

const { Option } = Select;
const { TextArea } = Input;

const ReviewTaskForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [inputKeyword, setInputKeyword] = useState('');
  const isEditing = !!id;

  // 自動保存的鍵
  const getAutoSaveKey = () => `review_task_form_${id || 'new'}`;

  // 加載測評任務數據
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 獲取所有店鋪
        const storesData = await api.stores.getAll() as Store[];
        setStores(storesData);

        if (isEditing) {
          // 獲取測評任務
          const taskData = await api.reviewTasks.getById(id as string) as ReviewTask;

          // 處理日期字段
          if (taskData.date) {
            (taskData as any).date = moment(taskData.date);
          }

          // 處理關鍵詞
          if (taskData.keywords) {
            try {
              const parsedKeywords = JSON.parse(taskData.keywords);
              setKeywords(parsedKeywords);
            } catch (error) {
              console.error('Error parsing keywords:', error);
            }
          }

          form.setFieldsValue(taskData);
        } else {
          // 嘗試從本地存儲加載草稿
          const savedData = await localforage.getItem(getAutoSaveKey()) as { taskData?: Partial<ReviewTask>; keywordsData?: string[] } | null;
          if (savedData) {
            const { taskData, keywordsData } = savedData;

            if (taskData) {
              // 處理日期字段
              if (taskData.date) {
                (taskData as any).date = moment(taskData.date);
              }

              form.setFieldsValue(taskData);
            }

            if (keywordsData) {
              setKeywords(keywordsData);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        message.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, form, isEditing, t]);

  // 自動保存表單數據
  useEffect(() => {
    const autoSaveInterval = setInterval(async () => {
      const formValues = form.getFieldsValue();
      if (Object.keys(formValues).length > 0) {
        await localforage.setItem(getAutoSaveKey(), {
          taskData: formValues,
          keywordsData: keywords,
        });
      }
    }, 30000); // 每 30 秒保存一次

    return () => clearInterval(autoSaveInterval);
  }, [form, keywords]);

  // 添加關鍵詞
  const handleAddKeyword = () => {
    if (inputKeyword && !keywords.includes(inputKeyword)) {
      setKeywords([...keywords, inputKeyword]);
      setInputKeyword('');
    }
  };

  // 刪除關鍵詞
  const handleRemoveKeyword = (removedKeyword: string) => {
    setKeywords(keywords.filter(keyword => keyword !== removedKeyword));
  };

  // 提交表單
  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      // 處理日期字段
      if (values.date) {
        values.date = values.date.format('YYYY-MM-DD');
      }

      // 處理關鍵詞
      values.keywords = JSON.stringify(keywords);

      if (isEditing) {
        await api.reviewTasks.update(id, values);
      } else {
        await api.reviewTasks.create(values);
      }

      // 清除本地存儲的草稿
      await localforage.removeItem(getAutoSaveKey());

      message.success(t('common.success'));
      navigate('/review-tasks');
    } catch (error) {
      console.error('Error saving review task:', error);
      message.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(isEditing ? `/review-tasks/${id}` : '/review-tasks')}
        >
          {t('common.back')}
        </Button>
      </Space>

      <Card title={isEditing ? t('reviewTasks.editTask') : t('reviewTasks.addTask')}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            review_status: '未評論',
            delivery_status: '未收貨',
            is_virtual: true,
          }}
        >
          <Form.Item
            name="task_id"
            label={t('reviewTasks.taskId')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="store_id"
            label={t('reviewTasks.storeId')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Select
              showSearch
              placeholder={t('reviewTasks.storeId')}
              optionFilterProp="children"
            >
              {stores.map(store => (
                <Option key={store.store_id} value={store.store_id}>
                  {store.store_id} - {store.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="platform"
            label={t('reviewTasks.platform')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('reviewTasks.keywords')}
          >
            <Space style={{ marginBottom: 8 }}>
              <Input
                value={inputKeyword}
                onChange={(e) => setInputKeyword(e.target.value)}
                onPressEnter={handleAddKeyword}
                placeholder={t('reviewTasks.keywords')}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddKeyword}
              >
                {t('common.add')}
              </Button>
            </Space>
            <div>
              {keywords.map((keyword, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => handleRemoveKeyword(keyword)}
                  style={{ marginBottom: 8 }}
                >
                  {keyword}
                </Tag>
              ))}
            </div>
          </Form.Item>

          <Form.Item
            name="review_account"
            label={t('reviewTasks.reviewAccount')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="account_alias"
            label={t('reviewTasks.accountAlias')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label={t('reviewTasks.phone')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="delivery_store"
            label={t('reviewTasks.deliveryStore')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="amount"
            label={t('reviewTasks.amount')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>

          <Form.Item
            name="order_id"
            label={t('reviewTasks.orderId')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="date"
            label={t('reviewTasks.date')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="review_status"
            label={t('reviewTasks.reviewStatus')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Select>
              <Option value="已評論">{t('reviewTasks.reviewStatuses.reviewed')}</Option>
              <Option value="未評論">{t('reviewTasks.reviewStatuses.unreviewed')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="delivery_status"
            label={t('reviewTasks.deliveryStatus')}
            rules={[{ required: true, message: t('common.error') }]}
          >
            <Select>
              <Option value="已收貨">{t('reviewTasks.deliveryStatuses.received')}</Option>
              <Option value="未收貨">{t('reviewTasks.deliveryStatuses.unreceived')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="exception_handling"
            label={t('reviewTasks.exceptionHandling')}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="remarks"
            label={t('reviewTasks.remarks')}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="is_virtual"
            label={t('reviewTasks.isVirtual')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              icon={<SaveOutlined />}
            >
              {t('common.save')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ReviewTaskForm;
